import { UserFilter } from '../analyze-filters';
import moment from 'moment';
import { Activity, ActivityDetailVm, TileViewForm } from '@/core/models/activity';
import { TimezonedDate } from '@/core/models/timezoned-date';
import { ReportSettings } from '@/core/models/report-settings.interface';

export class ActivityDetailVmMapper {
    public static mapToVms(
        activities: Activity[],
        reportSettings: ReportSettings,
        users: UserFilter[],
    ): ActivityDetailVm[] {
        return activities.map((activity) => this.mapToVm(activity, reportSettings, users));
    }

    private static mapToVm(activity: Activity, reportSettings: ReportSettings, users: UserFilter[]): ActivityDetailVm {
        /**
         * Hot fix: Incorrect date in timetable and export (#231)
         *
         * Use toUpperCase here because we have wrong database entries with lowercase time reportSettings.
         */
        const localStartDate = activity.startDate.clone();
        localStartDate.behaveAsTrackedLocal(true);
        const localEndDate = activity.endDate.clone();
        localEndDate.behaveAsTrackedLocal(true);

        const date = localStartDate.format(reportSettings.dateFormat.toUpperCase());

        const durationSeconds = activity.endDate.canonicalDiff(activity.startDate, 'seconds');
        const duration = this.formatDuration(durationSeconds, reportSettings, 'seconds');

        let mappedTiles: string[];
        if (activity.tiles_) {
            mappedTiles = activity.tiles_.map((tile: TileViewForm) => tile.name);
        } else {
            console.error('Unexpected API responses. tiles_ should always be present in the current version.');
            mappedTiles = activity.tileIds.map(() => '');
        }

        const timezoneSwitch = activity.startDate.getApiUtcOffset() !== activity.endDate.getApiUtcOffset();

        return {
            id: activity.id,
            userId: activity.userId,
            userName: users.find((u) => u.id === activity.userId)?.name ?? '',
            tiles: activity.tiles_ ?? [],
            tileNames: mappedTiles,
            tileIds: activity.tileIds,
            layer1: mappedTiles[0],
            layer2: mappedTiles[1],
            layer3: mappedTiles[2],
            note: activity.note,
            date,
            time: this.getTimeString(localStartDate, localEndDate, reportSettings.timeFormat),
            start: localStartDate,
            end: localEndDate,
            duration,
            durationSeconds: durationSeconds,
            billed: activity.billed,
            isChecked: false,
            timezoneSwitch,
            lock: activity.lock_,
        };
    }

    private static getTimeString(start: TimezonedDate, end: TimezonedDate, timeFormat: string): string {
        const startTime = start.format(timeFormat);
        const endTime = end.format(timeFormat);

        const dayDiff = end.dayOfYear() - start.dayOfYear();
        const nextDayMarker: string = dayDiff !== 0 ? ` (+${dayDiff})` : '';

        return startTime + ' - ' + endTime + nextDayMarker;
    }

    private static durationSecondsToHmmss(seconds: number, displaySeconds: boolean): string {
        seconds = Math.ceil(seconds);

        const s = Math.round(seconds % 60);
        const minutes = Math.floor(seconds / 60);
        const h = Math.floor(minutes / 60);
        const m = Math.round(minutes % 60);

        const min = m < 10 ? '0' + m : m;
        if (!displaySeconds) {
            return `${h}h ${min}m`;
        }

        const sec = s < 10 ? '0' + s : s;
        return `${h}h ${min}m ${sec}s`;
    }

    // eg. 1.5h
    public static secondsToDecimalHours(seconds: number): string {
        // Create a moment duration from the seconds
        const duration = moment.duration(seconds, 'seconds');
        // Calculate the total hours as a decimal
        let hours = duration.asHours();
        // Round to 2 decimal places
        hours = Math.round(hours * 100) / 100;
        // Convert hours to string
        let hoursString = hours.toFixed(2);
        // instead of using "." use "," for decimal separator
        hoursString = hoursString.replace('.', ',');
        return `${hoursString}h`;
    }

    public static formatDuration(
        duration: number,
        reportSettings: ReportSettings,
        unitType: 'seconds' | 'microseconds',
    ): string {
        const durationSeconds = unitType === 'seconds' ? duration : Math.floor(duration / 1_000_000);
        if (reportSettings.durationFormat == 'Decimal') {
            return this.secondsToDecimalHours(durationSeconds);
        } else {
            return this.durationSecondsToHmmss(durationSeconds, !reportSettings.roundingEnabled);
        }
    }

    /**
     * Rounds the duration to the nearest unit
     * @param duration the duration to round
     * @param roundingUnit The unit to round to in minutes
     * @param roundingType up, down or nearest
     * @returns the rounded duration in seconds
     *
     */
    private static roundDuration(duration: number, roundingUnit: number, roundingType: string): number {
        const roundingUnitInSeconds = roundingUnit * 60;
        if (roundingType === 'up') {
            return Math.ceil(duration / roundingUnitInSeconds) * roundingUnitInSeconds;
        } else if (roundingType === 'down') {
            return Math.floor(duration / roundingUnitInSeconds) * roundingUnitInSeconds;
        } else if (roundingType === 'nearest') {
            return Math.round(duration / roundingUnitInSeconds) * roundingUnitInSeconds;
        }
        return duration; // No rounding or 'none'
    }

    public static updateVmActivities(
        activities: Array<ActivityDetailVm>,
        formats: ReportSettings,
        originalActivities: Array<ActivityDetailVm>,
    ): void {
        activities.forEach((activity: ActivityDetailVm) => {
            const originalActivityDuration = originalActivities.find((a) => a.id === activity.id)?.durationSeconds;

            const orgDuration = originalActivityDuration ?? 0;
            const durationRounded = formats.roundingEnabled
                ? this.roundDuration(orgDuration, formats.durationRoundingUnit!, formats.durationRoundingType!)
                : orgDuration;
            activity.durationSeconds = durationRounded;

            const durationDiff = durationRounded - orgDuration;

            // TODO change this back to copy .add_() call when it is implemented...
            const endForTimeCalc = activity.end.clone();
            endForTimeCalc.add(durationDiff, 'seconds');

            activity.time = this.getTimeString(activity.start, endForTimeCalc, formats.timeFormat);

            activity.duration = this.formatDuration(activity.durationSeconds, formats, 'seconds');

            activity.date = activity.start.format(formats.dateFormat.toUpperCase());
        });
    }

    /**
     * Performs a deep copy of the ActivityDetailVm array preserving types.
     * @param activities The array to be deep-copied.
     */
    public static deepCopy(activities: Array<ActivityDetailVm>): Array<ActivityDetailVm> {
        const retVal: Array<ActivityDetailVm> = [];
        activities.forEach((activity) => {
            const cpy = Object.assign({}, activity);
            cpy.start = activity.start.clone();
            cpy.end = activity.end.clone();
            retVal.push(cpy);
        });
        return retVal;
    }
}
